import type { TeamCallPlan, TeamCallPriceIds } from '../types/TeamCallSubscription.type';

type Environment = 'local' | 'dev' | 'staging' | 'prod';

// Runtime overrides can select a deployment-specific Stripe catalog.
// Resolve them only in server callers; this module is also imported by the browser.
export const getTeamCallPriceIds = (stage: string): TeamCallPriceIds => ({
  plus:
    process.env.STRIPE_TEAM_CALLS_PLUS_PRICE_ID ||
    STRIPE_PLANS.STRIPE_TEAM_CALLS_PLUS[stage as Environment] ||
    '',
  pro:
    process.env.STRIPE_TEAM_CALLS_PRO_PRICE_ID ||
    STRIPE_PLANS.STRIPE_TEAM_CALLS_PRO[stage as Environment] ||
    '',
});

export const getTeamCallPlanForPrice = (
  stage: string,
  priceId: string
): Exclude<TeamCallPlan, 'included'> | undefined => {
  if (!priceId) return undefined;
  const prices = getTeamCallPriceIds(stage);
  if (
    priceId === prices.pro ||
    priceId === STRIPE_PLANS.STRIPE_TEAM_CALLS_PRO_LEGACY[stage as Environment]
  ) {
    return 'pro';
  }
  if (
    priceId === prices.plus ||
    priceId === STRIPE_PLANS.STRIPE_TEAM_CALLS_PLUS_LEGACY[stage as Environment]
  ) {
    return 'plus';
  }
  return undefined;
};

export const STRIPE_PLANS = {
  STRIPE_TEAM_CALLS_PLUS: {
    local: 'price_1UTQy1AgtJKM80yB6CJ1Uh5Z',
    dev: 'price_1UTQy1AgtJKM80yB6CJ1Uh5Z',
    staging: 'price_1UTQy1AgtJKM80yB6CJ1Uh5Z',
    prod: 'price_1UTQuhAgtJKM80yBbLyfJtpD',
  },
  STRIPE_TEAM_CALLS_PRO: {
    local: 'price_1UTQyCAgtJKM80yBjUYk7gez',
    dev: 'price_1UTQyCAgtJKM80yBjUYk7gez',
    staging: 'price_1UTQyCAgtJKM80yBjUYk7gez',
    prod: 'price_1UTQv5AgtJKM80yB6wtYmca1',
  },
  STRIPE_TEAM_CALLS_PLUS_LEGACY: {
    local: 'price_1UTO9mAgtJKM80yBuVXIo2Cv',
    dev: 'price_1UTO9mAgtJKM80yBuVXIo2Cv',
    staging: 'price_1UTO9mAgtJKM80yBuVXIo2Cv',
    prod: 'price_1UTO6LAgtJKM80yB4EdoB4fF',
  },
  STRIPE_TEAM_CALLS_PRO_LEGACY: {
    local: 'price_1UTO9yAgtJKM80yBiRMsxAzw',
    dev: 'price_1UTO9yAgtJKM80yBiRMsxAzw',
    staging: 'price_1UTO9yAgtJKM80yBiRMsxAzw',
    prod: 'price_1UTO6mAgtJKM80yB3C8quiWl',
  },
  STRIPE_MASS_EMAIL: {
    local: 'price_1UTQyeAgtJKM80yBNqXZuNPU',
    dev: 'price_1UTQyeAgtJKM80yBNqXZuNPU',
    staging: 'price_1UTQyeAgtJKM80yBNqXZuNPU',
    prod: 'price_1UTQvHAgtJKM80yBcyGdPU0e',
  },
  STRIPE_CAMPAIGN_EMAIL_OVERAGE: {
    local: 'price_1UTQzPAgtJKM80yBCNL7la2R',
    dev: 'price_1UTQzPAgtJKM80yBCNL7la2R',
    staging: 'price_1UTQzPAgtJKM80yBCNL7la2R',
    prod: 'price_1UTQw1AgtJKM80yBZYYg6yak',
  },
  // Existing subscriptions retain these standalone-product prices.
  STRIPE_MASS_EMAIL_LEGACY: {
    local: 'price_1USJ2sAgtJKM80yBftuTAbXU',
    dev: 'price_1USJ2sAgtJKM80yBftuTAbXU',
    staging: 'price_1USJ2sAgtJKM80yBftuTAbXU',
    prod: 'price_1USIuTAgtJKM80yB84lo29ki',
  },
  STRIPE_CAMPAIGN_EMAIL_OVERAGE_LEGACY: {
    local: 'price_1USJ2sAgtJKM80yB1SxAXLK1',
    dev: 'price_1USJ2sAgtJKM80yB1SxAXLK1',
    staging: 'price_1USJ2sAgtJKM80yB1SxAXLK1',
    prod: 'price_1UNYHiAgtJKM80yBEq6tAKQV',
  },
  STRIPE_CAMPAIGN_EMAIL_OVERAGE_LEGACY_V1: {
    local: 'price_1UNYNuAgtJKM80yBd0fTMVxG',
    dev: 'price_1UNYNuAgtJKM80yBd0fTMVxG',
    staging: 'price_1UNYNuAgtJKM80yBd0fTMVxG',
    prod: 'price_1UNYHiAgtJKM80yBEq6tAKQV',
  },
  STRIPE_EHR_PLAN: {
    local: 'price_1OG7fDAgtJKM80yBRjSFbADO',
    dev: 'price_1OG7fDAgtJKM80yBRjSFbADO',
    staging: 'price_1OG7fDAgtJKM80yBRjSFbADO',
    prod: 'price_1PgD3QAgtJKM80yB71EQx0Bk',
  },
  STRIPE_BASIC_PLAN: {
    local: 'price_1KlyjSAgtJKM80yBrFac5BgG',
    dev: 'price_1KlyjSAgtJKM80yBrFac5BgG',
    staging: 'price_1KlyjSAgtJKM80yBrFac5BgG',
    prod: 'price_1L9AIpAgtJKM80yBNs6I1wdJ',
  },
  STRIPE_PLUS_PLAN: {
    local: 'price_1KlyjhAgtJKM80yBfl37ekuV',
    dev: 'price_1KlyjhAgtJKM80yBfl37ekuV',
    staging: 'price_1KlyjhAgtJKM80yBfl37ekuV',
    prod: 'price_1L9AIjAgtJKM80yB9n16DdSl',
  },
  STRIPE_PREMIUM_PLAN: {
    local: 'price_1KlyjtAgtJKM80yB8DmZzTHp',
    dev: 'price_1KlyjtAgtJKM80yB8DmZzTHp',
    staging: 'price_1KlyjtAgtJKM80yB8DmZzTHp',
    prod: 'price_1L9ABnAgtJKM80yBCOzvWaxf',
  },
  STRIPE_CANADA_BASIC_PLAN: {
    local: 'price_1Kpy9MAgtJKM80yBYnvnroMr',
    dev: 'price_1Kpy9MAgtJKM80yBYnvnroMr',
    staging: 'price_1Kpy9MAgtJKM80yBYnvnroMr',
    prod: 'price_1L9AIpAgtJKM80yB88vzj8Ya',
  },
  STRIPE_CANADA_PLUS_PLAN: {
    local: 'price_1Kq14eAgtJKM80yB0lu3cP7C',
    dev: 'price_1Kq14eAgtJKM80yB0lu3cP7C',
    staging: 'price_1Kq14eAgtJKM80yB0lu3cP7C',
    prod: 'price_1L9AIjAgtJKM80yBIeFdSLII',
  },
  STRIPE_CANADA_PREMIUM_PLAN: {
    local: 'price_1KpyUyAgtJKM80yBHhEpJgr3',
    dev: 'price_1KpyUyAgtJKM80yBHhEpJgr3',
    staging: 'price_1KpyUyAgtJKM80yBHhEpJgr3',
    prod: 'price_1L9ABnAgtJKM80yBHsxoxsMp',
  },
  STRIPE_ZAF_BASIC_PLAN: {
    local: 'price_1U5nwXAgtJKM80yBSGCxaaRZ',
    dev: 'price_1U5nwXAgtJKM80yBSGCxaaRZ',
    staging: 'price_1U5nwXAgtJKM80yBSGCxaaRZ',
    prod: 'price_1U5nwXAgtJKM80yBSGCxaaRZ',
  },
  STRIPE_ZAF_PLUS_PLAN: {
    local: 'price_1U5nlbAgtJKM80yBoa22ze7b',
    dev: 'price_1U5nlbAgtJKM80yBoa22ze7b',
    staging: 'price_1U5nlbAgtJKM80yBoa22ze7b',
    prod: 'price_1U5nlbAgtJKM80yBoa22ze7b',
  },
  STRIPE_ZAF_PREMIUM_PLAN: {
    local: 'price_1U5nlGAgtJKM80yBST0MVaga',
    dev: 'price_1U5nlGAgtJKM80yBST0MVaga',
    staging: 'price_1U5nlGAgtJKM80yBST0MVaga',
    prod: 'price_1U5nlGAgtJKM80yBST0MVaga',
  },
  STRIPE_ZAF_ADDITIONAL_LOCATION: {
    local: 'price_1U5o2gAgtJKM80yBlFyaORem',
    dev: 'price_1U5o2gAgtJKM80yBlFyaORem',
    staging: 'price_1U5o2gAgtJKM80yBlFyaORem',
    prod: 'price_1U5o2gAgtJKM80yBlFyaORem',
  },
  STRIPE_BASIC_PLAN_ANNUAL: {
    local: 'price_1Kq1VUAgtJKM80yBFHautCj2',
    dev: 'price_1Kq1VUAgtJKM80yBFHautCj2',
    staging: 'price_1Kq1VUAgtJKM80yBFHautCj2',
    prod: 'price_1L9AIpAgtJKM80yBTM5hjDX1',
  },
  STRIPE_PLUS_PLAN_ANNUAL: {
    local: 'price_1Kq1WAAgtJKM80yBQFeScxk9',
    dev: 'price_1Kq1WAAgtJKM80yBQFeScxk9',
    staging: 'price_1Kq1WAAgtJKM80yBQFeScxk9',
    prod: 'price_1L9AIjAgtJKM80yBDhXcdaQJ',
  },
  STRIPE_PREMIUM_PLAN_ANNUAL: {
    local: 'price_1Kq1WpAgtJKM80yB2MUBgyf0',
    dev: 'price_1Kq1WpAgtJKM80yB2MUBgyf0',
    staging: 'price_1Kq1WpAgtJKM80yB2MUBgyf0',
    prod: 'price_1L9ABnAgtJKM80yBZanzDgiI',
  },
  STRIPE_CANADA_BASIC_PLAN_ANNUAL: {
    local: 'price_1Kq1VhAgtJKM80yBP6QgfaEG',
    dev: 'price_1Kq1VhAgtJKM80yBP6QgfaEG',
    staging: 'price_1Kq1VhAgtJKM80yBP6QgfaEG',
    prod: 'price_1L9AIpAgtJKM80yBPShU8RLj',
  },
  STRIPE_CANADA_PLUS_PLAN_ANNUAL: {
    local: 'price_1Kq1WMAgtJKM80yB2QPZr6Bt',
    dev: 'price_1Kq1WMAgtJKM80yB2QPZr6Bt',
    staging: 'price_1Kq1WMAgtJKM80yB2QPZr6Bt',
    prod: 'price_1L9AIjAgtJKM80yBZYx36jUS',
  },
  STRIPE_CANADA_PREMIUM_PLAN_ANNUAL: {
    local: 'price_1Kq1WzAgtJKM80yBk6XqzhAl',
    dev: 'price_1Kq1WzAgtJKM80yBk6XqzhAl',
    staging: 'price_1Kq1WzAgtJKM80yBk6XqzhAl',
    prod: 'price_1L9ABnAgtJKM80yBwQyYGW1o',
  },
  STRIPE_PRODUCT_BASIC_PLAN: {
    local: 'prod_LSuYgDL8LT1Maa',
    dev: 'prod_LSuYgDL8LT1Maa',
    staging: 'prod_LSuYgDL8LT1Maa',
    prod: 'prod_Lqs22wX1rxdrJx',
  },
  STRIPE_PRODUCT_PLUS_PLAN: {
    local: 'prod_LSuYJ2QNKuVvD0',
    dev: 'prod_LSuYJ2QNKuVvD0',
    staging: 'prod_LSuYJ2QNKuVvD0',
    prod: 'prod_Lqs2YJnLLQN1jx',
  },
  STRIPE_PRODUCT_PREMIUM_PLAN: {
    local: 'prod_LSuZShSVAZ9uSR',
    dev: 'prod_LSuZShSVAZ9uSR',
    staging: 'prod_LSuZShSVAZ9uSR',
    prod: 'prod_LqrvuZSV4BsVm6',
  },
  STRIPE_PRODUCT_EHR_PLAN: {
    local: 'TBD',
    dev: 'TBD',
    staging: 'TBD',
    prod: 'prod_QXHc2ZPxuuePoA',
  },
  STRIPE_PRODUCT_LOCATIONS_PLAN: {
    local: 'TBD',
    dev: 'TBD',
    staging: 'TBD',
    prod: 'prod_NsVReFb1T5kLzA',
  },
  STRIPE_PRODUCT_ADDITIONAL_PROVIDERS_PLAN: {
    local: 'TBD',
    dev: 'TBD',
    staging: 'TBD',
    prod: 'prod_QXHdMOltfxd0o5',
  },
  STRIPE_PRODUCT_SOCIAL_MEDIA_ADDON_PLAN: {
    local: 'TBD',
    dev: 'TBD',
    staging: 'TBD',
    prod: 'prod_QZ2O7Mfbf6cvUW',
  },
  STRIPE_PRODUCT_NEWSLETTERS_PLAN: {
    local: 'TBD',
    dev: 'TBD',
    staging: 'TBD',
    prod: 'prod_QZ2P5uvYC2LkYC',
  },
  STRIPE_PRODUCT_X12_LICENSURE_FEE_PLAN: {
    local: 'TBD',
    dev: 'TBD',
    staging: 'TBD',
    prod: 'prod_RPx2ItSwfZvfWI',
  },
  STRIPE_PRODUCT_CLEARING_HOUSE_INTEGRATIONS_PLAN: {
    // Test mode's "Trizetto Integration" product mirrors prod's "Clearinghouse Integration".
    local: 'prod_Q5AHUm7Nt1oDlR',
    dev: 'prod_Q5AHUm7Nt1oDlR',
    staging: 'prod_Q5AHUm7Nt1oDlR',
    prod: 'prod_Qr2fz3sDLG0RUa',
  },
  STRIPE_PRODUCT_VOICE_TO_CHART_PLAN: {
    local: 'TBD',
    dev: 'TBD',
    staging: 'TBD',
    prod: 'prod_SYNxsCtoEVYlBv',
  },
  STRIPE_PRODUCT_ADVANCED_SMS_PLAN: {
    local: 'prod_SWsGI8EozNoERD',
    dev: 'prod_SWsGI8EozNoERD',
    staging: 'prod_SWsGI8EozNoERD',
    prod: 'prod_SYNxrWFDNwfZF9',
  },
  STRIPE_ADDITIONAL_LOCATION: {
    local: 'price_1MjmYBAgtJKM80yBN165a1RV',
    dev: 'price_1MjmYBAgtJKM80yBN165a1RV',
    staging: 'price_1MjmYBAgtJKM80yBN165a1RV',
    prod: 'price_1N6kQiAgtJKM80yBtmq9D97k',
  },
  STRIPE_ADDITIONAL_LOCATION_ANNUAL: {
    local: 'price_1MjmfhAgtJKM80yB0FTPyagW',
    dev: 'price_1MjmfhAgtJKM80yB0FTPyagW',
    staging: 'price_1MjmfhAgtJKM80yB0FTPyagW',
    prod: 'price_1N6kQiAgtJKM80yBRS2DS9G1',
  },
  STRIPE_VOICE_TO_CHART: {
    local: 'price_1RboYFAgtJKM80yBSWnaZiB2',
    dev: 'price_1RboYFAgtJKM80yBSWnaZiB2',
    staging: 'price_1RboYFAgtJKM80yBSWnaZiB2',
    prod: 'price_1RdHBgAgtJKM80yBXz7hV4ay',
  },
  STRIPE_VOICE_TO_CHART_OVERAGE: {
    local: 'price_1RboZCAgtJKM80yBZJPdfLRH',
    dev: 'price_1RboZCAgtJKM80yBZJPdfLRH',
    staging: 'price_1RboZCAgtJKM80yBZJPdfLRH',
    prod: 'price_1RdHBgAgtJKM80yB5OjAz53V',
  },
  // AI Receptionist introductory offer: $99/mo per number ($149 standard),
  // 300 included equivalent minutes + $0.25/equivalent-minute overage.
  // Stripe receives integer quarter-minute units via the
  // 'ai_receptionist_usage_units' meter (one unit is 0.25 minute).
  // TODO(prod): create the live-mode prices and paste the IDs — subscribe/
  // entitlement code fails closed while empty.
  STRIPE_AI_RECEPTIONIST: {
    local: 'price_1U0OSDAgtJKM80yBULHyQPne',
    dev: 'price_1U0OSDAgtJKM80yBULHyQPne',
    staging: 'price_1U0OSDAgtJKM80yBULHyQPne',
    prod: 'price_1U0WyuAgtJKM80yBiSL6b96G',
  },
  // The metered price is 6.25 cents per quarter-minute unit, which is exactly
  // $0.25 per customer-facing minute. Keep it on the dedicated
  // 'ai_receptionist_usage_units' meter.
  STRIPE_AI_RECEPTIONIST_OVERAGE: {
    local: 'price_1U0OSPAgtJKM80yBEfHYTXRu',
    dev: 'price_1U0OSPAgtJKM80yBEfHYTXRu',
    staging: 'price_1U0OSPAgtJKM80yBEfHYTXRu',
    prod: 'price_1U0WyuAgtJKM80yBRY1uskvK',
  },
  // Recognize subscriptions created before the August 2026 pricing change
  // until their Stripe items are migrated to the current prices.
  STRIPE_AI_RECEPTIONIST_LEGACY: {
    local: 'price_1TputHAgtJKM80yBMaUqxmAa',
    dev: 'price_1TputHAgtJKM80yBMaUqxmAa',
    staging: 'price_1TputHAgtJKM80yBMaUqxmAa',
    prod: '',
  },
  STRIPE_AI_RECEPTIONIST_OVERAGE_LEGACY: {
    local: 'price_1TpuvwAgtJKM80yBQwHMqnnD',
    dev: 'price_1TpuvwAgtJKM80yBQwHMqnnD',
    staging: 'price_1TpuvwAgtJKM80yBQwHMqnnD',
    prod: '',
  },
  // ChiroUp Clearinghouse Services (Stedi), a subscription separate from the core plan:
  // $39/mo base (coupon-discounted for TriZetto/Infinedi subscribers) plus legacy metered
  // ('sum' aggregation) monthly prices of $0.15/claim, $0.10/ERA, $0.10/eligibility.
  // All four active prices belong to one "ChiroUp Clearinghouse" product. Legacy metered prices need API
  // version 2023-10-16 or earlier: Stripe Shell can create them in test mode, but live mode
  // needs the API directly (Shell is read-only there and the Dashboard requires a meter).
  // Enrollment validates each price's amount and shape and fails closed while any is empty.
  STRIPE_STEDI_CLEARINGHOUSE_BASE: {
    local: 'price_1UWzXiAgtJKM80yB5ral7BYU',
    dev: 'price_1UWzXiAgtJKM80yB5ral7BYU',
    staging: 'price_1UWzXiAgtJKM80yB5ral7BYU',
    prod: 'price_1UX0SDAgtJKM80yBcd1UlYey',
  },
  // Archived prices retained only to read historical subscriptions and invoices.
  STRIPE_STEDI_CLEARINGHOUSE_WAIVED_BASE: {
    local: 'price_1UWzY2AgtJKM80yBj8bPjwhr',
    dev: 'price_1UWzY2AgtJKM80yBj8bPjwhr',
    staging: 'price_1UWzY2AgtJKM80yBj8bPjwhr',
    prod: 'price_1UX0SEAgtJKM80yBTcEyIxMp',
  },
  STRIPE_STEDI_CLEARINGHOUSE_BASE_FREE_COUPON: {
    local: 'CHIROUP_CLEARINGHOUSE_BASE_FREE',
    dev: 'CHIROUP_CLEARINGHOUSE_BASE_FREE',
    staging: 'CHIROUP_CLEARINGHOUSE_BASE_FREE',
    prod: 'CHIROUP_CLEARINGHOUSE_BASE_FREE',
  },
  STRIPE_STEDI_CLEARINGHOUSE_CLAIM: {
    local: 'price_1UX8E9AgtJKM80yBIlRWI20E',
    dev: 'price_1UX8E9AgtJKM80yBIlRWI20E',
    staging: 'price_1UX8E9AgtJKM80yBIlRWI20E',
    prod: 'price_1UX8FpAgtJKM80yBteszeLod',
  },
  STRIPE_STEDI_CLEARINGHOUSE_ERA: {
    local: 'price_1UX8E9AgtJKM80yBste0tmpD',
    dev: 'price_1UX8E9AgtJKM80yBste0tmpD',
    staging: 'price_1UX8E9AgtJKM80yBste0tmpD',
    prod: 'price_1UX8FpAgtJKM80yBmf6eieKl',
  },
  STRIPE_STEDI_CLEARINGHOUSE_ELIGIBILITY: {
    local: 'price_1UX8EAAgtJKM80yB1UAd8rjv',
    dev: 'price_1UX8EAAgtJKM80yB1UAd8rjv',
    staging: 'price_1UX8EAAgtJKM80yB1UAd8rjv',
    prod: 'price_1UX8FpAgtJKM80yBw3Dls736',
  },
  STRIPE_ADVANCED_MESSAGING: {
    local: 'price_1RboW8AgtJKM80yBRKxlfG1Z',
    dev: 'price_1RboW8AgtJKM80yBRKxlfG1Z',
    staging: 'price_1RboW8AgtJKM80yBRKxlfG1Z',
    prod: 'price_1RdHBtAgtJKM80yBRp0h64eK',
  },
  STRIPE_ADVANCED_MESSAGING_OVERAGE: {
    local: 'price_1RboWeAgtJKM80yBKcYtpQgN',
    dev: 'price_1RboWeAgtJKM80yBKcYtpQgN',
    staging: 'price_1RboWeAgtJKM80yBKcYtpQgN',
    prod: 'price_1RdHBtAgtJKM80yBaK8IYoq8',
  },
  STRIPE_CANADA_ADVANCED_MESSAGING: {
    local: 'price_1SCgLdAgtJKM80yBoTayvcR5',
    dev: 'price_1SCgLdAgtJKM80yBoTayvcR5',
    staging: 'price_1SCgLdAgtJKM80yBoTayvcR5',
    prod: 'price_1RiNnDAgtJKM80yBqwyea3ol',
  },
  STRIPE_CANADA_ADVANCED_MESSAGING_OVERAGE: {
    local: 'price_1SCgMKAgtJKM80yBMPtPU4iF',
    dev: 'price_1SCgMKAgtJKM80yBMPtPU4iF',
    staging: 'price_1SCgMKAgtJKM80yBMPtPU4iF',
    prod: 'price_1RiNnkAgtJKM80yBR7ud7Kh3',
  },
};

export const createPrices = (env: string) => {
  const envTyped = env as Environment;
  const STRIPE_EHR_PLAN = STRIPE_PLANS.STRIPE_EHR_PLAN[envTyped] as string;
  const STRIPE_BASIC_PLAN = STRIPE_PLANS.STRIPE_BASIC_PLAN[envTyped] as string;
  const STRIPE_PLUS_PLAN = STRIPE_PLANS.STRIPE_PLUS_PLAN[envTyped] as string;
  const STRIPE_PREMIUM_PLAN = STRIPE_PLANS.STRIPE_PREMIUM_PLAN[envTyped] as string;
  const STRIPE_CANADA_BASIC_PLAN = STRIPE_PLANS.STRIPE_CANADA_BASIC_PLAN[envTyped] as string;
  const STRIPE_CANADA_PLUS_PLAN = STRIPE_PLANS.STRIPE_CANADA_PLUS_PLAN[envTyped] as string;
  const STRIPE_CANADA_PREMIUM_PLAN = STRIPE_PLANS.STRIPE_CANADA_PREMIUM_PLAN[envTyped] as string;
  const STRIPE_ZAF_BASIC_PLAN = STRIPE_PLANS.STRIPE_ZAF_BASIC_PLAN[envTyped] as string;
  const STRIPE_ZAF_PLUS_PLAN = STRIPE_PLANS.STRIPE_ZAF_PLUS_PLAN[envTyped] as string;
  const STRIPE_ZAF_PREMIUM_PLAN = STRIPE_PLANS.STRIPE_ZAF_PREMIUM_PLAN[envTyped] as string;
  const STRIPE_ZAF_ADDITIONAL_LOCATION = STRIPE_PLANS.STRIPE_ZAF_ADDITIONAL_LOCATION[
    envTyped
  ] as string;
  const STRIPE_BASIC_PLAN_ANNUAL = STRIPE_PLANS.STRIPE_BASIC_PLAN_ANNUAL[envTyped] as string;
  const STRIPE_PLUS_PLAN_ANNUAL = STRIPE_PLANS.STRIPE_PLUS_PLAN_ANNUAL[envTyped] as string;
  const STRIPE_PREMIUM_PLAN_ANNUAL = STRIPE_PLANS.STRIPE_PREMIUM_PLAN_ANNUAL[envTyped] as string;
  const STRIPE_CANADA_BASIC_PLAN_ANNUAL = STRIPE_PLANS.STRIPE_CANADA_BASIC_PLAN_ANNUAL[
    envTyped
  ] as string;
  const STRIPE_CANADA_PLUS_PLAN_ANNUAL = STRIPE_PLANS.STRIPE_CANADA_PLUS_PLAN_ANNUAL[
    envTyped
  ] as string;
  const STRIPE_CANADA_PREMIUM_PLAN_ANNUAL = STRIPE_PLANS.STRIPE_CANADA_PREMIUM_PLAN_ANNUAL[
    envTyped
  ] as string;
  const STRIPE_ADDITIONAL_LOCATION = STRIPE_PLANS.STRIPE_ADDITIONAL_LOCATION[envTyped] as string;
  const STRIPE_ADDITIONAL_LOCATION_ANNUAL = STRIPE_PLANS.STRIPE_ADDITIONAL_LOCATION_ANNUAL[
    envTyped
  ] as string;

  // New product ID constants
  const STRIPE_PRODUCT_EHR_PLAN = STRIPE_PLANS.STRIPE_PRODUCT_EHR_PLAN[envTyped] as string;
  const STRIPE_PRODUCT_LOCATIONS_PLAN = STRIPE_PLANS.STRIPE_PRODUCT_LOCATIONS_PLAN[
    envTyped
  ] as string;
  const STRIPE_PRODUCT_ADDITIONAL_PROVIDERS_PLAN = STRIPE_PLANS
    .STRIPE_PRODUCT_ADDITIONAL_PROVIDERS_PLAN[envTyped] as string;
  const STRIPE_PRODUCT_SOCIAL_MEDIA_ADDON_PLAN = STRIPE_PLANS
    .STRIPE_PRODUCT_SOCIAL_MEDIA_ADDON_PLAN[envTyped] as string;
  const STRIPE_PRODUCT_NEWSLETTERS_PLAN = STRIPE_PLANS.STRIPE_PRODUCT_NEWSLETTERS_PLAN[
    envTyped
  ] as string;
  const STRIPE_PRODUCT_X12_LICENSURE_FEE_PLAN = STRIPE_PLANS.STRIPE_PRODUCT_X12_LICENSURE_FEE_PLAN[
    envTyped
  ] as string;
  const STRIPE_PRODUCT_CLEARING_HOUSE_INTEGRATIONS_PLAN = STRIPE_PLANS
    .STRIPE_PRODUCT_CLEARING_HOUSE_INTEGRATIONS_PLAN[envTyped] as string;
  const STRIPE_PRODUCT_VOICE_TO_CHART_PLAN = STRIPE_PLANS.STRIPE_PRODUCT_VOICE_TO_CHART_PLAN[
    envTyped
  ] as string;

  const prices = {
    US: {
      ehr: STRIPE_EHR_PLAN,
      basic: STRIPE_BASIC_PLAN,
      plus: STRIPE_PLUS_PLAN,
      premium: STRIPE_PREMIUM_PLAN,
      basicAnnual: STRIPE_BASIC_PLAN_ANNUAL,
      plusAnnual: STRIPE_PLUS_PLAN_ANNUAL,
      premiumAnnual: STRIPE_PREMIUM_PLAN_ANNUAL,
      additionalLocations: STRIPE_ADDITIONAL_LOCATION,
      additionalLocationsAnnual: STRIPE_ADDITIONAL_LOCATION_ANNUAL,
    },
    CAN: {
      basic: STRIPE_CANADA_BASIC_PLAN,
      plus: STRIPE_CANADA_PLUS_PLAN,
      premium: STRIPE_CANADA_PREMIUM_PLAN,
      basicAnnual: STRIPE_CANADA_BASIC_PLAN_ANNUAL,
      plusAnnual: STRIPE_CANADA_PLUS_PLAN_ANNUAL,
      premiumAnnual: STRIPE_CANADA_PREMIUM_PLAN_ANNUAL,
      additionalLocations: STRIPE_ADDITIONAL_LOCATION,
      additionalLocationsAnnual: STRIPE_ADDITIONAL_LOCATION_ANNUAL,
    },
    ZAF: {
      basic: STRIPE_ZAF_BASIC_PLAN,
      plus: STRIPE_ZAF_PLUS_PLAN,
      premium: STRIPE_ZAF_PREMIUM_PLAN,
      additionalLocations: STRIPE_ZAF_ADDITIONAL_LOCATION,
    },
  } as {
    US: {
      [key: string]: string;
    };
    CAN: {
      [key: string]: string;
    };
    ZAF: {
      [key: string]: string;
    };
  };

  const planNames = {
    [STRIPE_EHR_PLAN]: 'ehr',
    [STRIPE_BASIC_PLAN]: 'basic',
    [STRIPE_PLUS_PLAN]: 'plus',
    [STRIPE_PREMIUM_PLAN]: 'premium',
    [STRIPE_CANADA_BASIC_PLAN]: 'basic',
    [STRIPE_CANADA_PLUS_PLAN]: 'plus',
    [STRIPE_CANADA_PREMIUM_PLAN]: 'premium',
    [STRIPE_ZAF_BASIC_PLAN]: 'basic',
    [STRIPE_ZAF_PLUS_PLAN]: 'plus',
    [STRIPE_ZAF_PREMIUM_PLAN]: 'premium',
    [STRIPE_BASIC_PLAN_ANNUAL]: 'basicAnnual',
    [STRIPE_PLUS_PLAN_ANNUAL]: 'plusAnnual',
    [STRIPE_PREMIUM_PLAN_ANNUAL]: 'premiumAnnual',
    [STRIPE_CANADA_BASIC_PLAN_ANNUAL]: 'basicAnnual',
    [STRIPE_CANADA_PLUS_PLAN_ANNUAL]: 'plusAnnual',
    [STRIPE_CANADA_PREMIUM_PLAN_ANNUAL]: 'premiumAnnual',
  };

  const planTypes = {
    [STRIPE_EHR_PLAN]: 'ehr',
    [STRIPE_BASIC_PLAN]: 'basic',
    [STRIPE_PLUS_PLAN]: 'plus',
    [STRIPE_PREMIUM_PLAN]: 'premium',
    [STRIPE_CANADA_BASIC_PLAN]: 'basic',
    [STRIPE_CANADA_PLUS_PLAN]: 'plus',
    [STRIPE_CANADA_PREMIUM_PLAN]: 'premium',
    [STRIPE_ZAF_BASIC_PLAN]: 'basic',
    [STRIPE_ZAF_PLUS_PLAN]: 'plus',
    [STRIPE_ZAF_PREMIUM_PLAN]: 'premium',
    [STRIPE_BASIC_PLAN_ANNUAL]: 'basic',
    [STRIPE_PLUS_PLAN_ANNUAL]: 'plus',
    [STRIPE_PREMIUM_PLAN_ANNUAL]: 'premium',
    [STRIPE_CANADA_BASIC_PLAN_ANNUAL]: 'basic',
    [STRIPE_CANADA_PLUS_PLAN_ANNUAL]: 'plus',
    [STRIPE_CANADA_PREMIUM_PLAN_ANNUAL]: 'premium',
  };

  const planNamesForSalesforce = {
    [STRIPE_EHR_PLAN]: 'EHR',
    [STRIPE_BASIC_PLAN]: 'Basic',
    [STRIPE_PLUS_PLAN]: 'Plus',
    [STRIPE_PREMIUM_PLAN]: 'Premium',
    [STRIPE_CANADA_BASIC_PLAN]: 'Basic',
    [STRIPE_CANADA_PLUS_PLAN]: 'Plus',
    [STRIPE_CANADA_PREMIUM_PLAN]: 'Premium',
    [STRIPE_ZAF_BASIC_PLAN]: 'Basic',
    [STRIPE_ZAF_PLUS_PLAN]: 'Plus',
    [STRIPE_ZAF_PREMIUM_PLAN]: 'Premium',
    [STRIPE_BASIC_PLAN_ANNUAL]: 'Basic',
    [STRIPE_PLUS_PLAN_ANNUAL]: 'Plus',
    [STRIPE_PREMIUM_PLAN_ANNUAL]: 'Premium',
    [STRIPE_CANADA_BASIC_PLAN_ANNUAL]: 'Basic',
    [STRIPE_CANADA_PLUS_PLAN_ANNUAL]: 'Plus',
    [STRIPE_CANADA_PREMIUM_PLAN_ANNUAL]: 'Premium',
  };

  const allPlansIds = [
    STRIPE_EHR_PLAN,
    STRIPE_BASIC_PLAN,
    STRIPE_PLUS_PLAN,
    STRIPE_PREMIUM_PLAN,
    STRIPE_CANADA_BASIC_PLAN,
    STRIPE_CANADA_PLUS_PLAN,
    STRIPE_CANADA_PREMIUM_PLAN,
    STRIPE_ZAF_BASIC_PLAN,
    STRIPE_ZAF_PLUS_PLAN,
    STRIPE_ZAF_PREMIUM_PLAN,
  ];

  const additionalLocationsIds = [
    STRIPE_ADDITIONAL_LOCATION,
    STRIPE_ADDITIONAL_LOCATION_ANNUAL,
    STRIPE_ZAF_ADDITIONAL_LOCATION,
  ];

  const pricesToProducts = {
    [prices.US.ehr]: STRIPE_PLANS.STRIPE_EHR_PLAN[envTyped],
    [prices.US.basic]: STRIPE_PLANS.STRIPE_PRODUCT_BASIC_PLAN[envTyped],
    [prices.US.plus]: STRIPE_PLANS.STRIPE_PRODUCT_PLUS_PLAN[envTyped],
    [prices.US.premium]: STRIPE_PLANS.STRIPE_PRODUCT_PREMIUM_PLAN[envTyped],
    [prices.CAN.basic]: STRIPE_PLANS.STRIPE_PRODUCT_BASIC_PLAN[envTyped],
    [prices.CAN.plus]: STRIPE_PLANS.STRIPE_PRODUCT_PLUS_PLAN[envTyped],
    [prices.CAN.premium]: STRIPE_PLANS.STRIPE_PRODUCT_PREMIUM_PLAN[envTyped],
    [prices.ZAF.basic]: STRIPE_PLANS.STRIPE_PRODUCT_BASIC_PLAN.prod,
    [prices.ZAF.plus]: STRIPE_PLANS.STRIPE_PRODUCT_PLUS_PLAN.prod,
    [prices.ZAF.premium]: STRIPE_PLANS.STRIPE_PRODUCT_PREMIUM_PLAN.prod,
    [prices.ZAF.additionalLocations]: STRIPE_PLANS.STRIPE_PRODUCT_LOCATIONS_PLAN.prod,
  } as {
    [key: string]: string;
  };

  const productsToPlans = {
    [STRIPE_PLANS.STRIPE_EHR_PLAN[envTyped] as string]: 'ehr',
    [STRIPE_PLANS.STRIPE_PRODUCT_BASIC_PLAN[envTyped] as string]: 'basic',
    [STRIPE_PLANS.STRIPE_PRODUCT_PLUS_PLAN[envTyped] as string]: 'plus',
    [STRIPE_PLANS.STRIPE_PRODUCT_PREMIUM_PLAN[envTyped] as string]: 'premium',
    [STRIPE_PRODUCT_EHR_PLAN]: 'ehr',
    [STRIPE_PRODUCT_LOCATIONS_PLAN]: 'locations',
    [STRIPE_PRODUCT_ADDITIONAL_PROVIDERS_PLAN]: 'additional_providers',
    [STRIPE_PRODUCT_SOCIAL_MEDIA_ADDON_PLAN]: 'social_media_addon',
    [STRIPE_PRODUCT_NEWSLETTERS_PLAN]: 'newsletters',
    [STRIPE_PRODUCT_X12_LICENSURE_FEE_PLAN]: 'x12_licensure_fee',
    [STRIPE_PRODUCT_CLEARING_HOUSE_INTEGRATIONS_PLAN]: 'clearing_house_integrations',
    [STRIPE_PRODUCT_VOICE_TO_CHART_PLAN]: 'voice_to_chart',
  };

  const plans = {
    basic: 149,
    plus: 199,
    premium: 249,
    additionalLocations: 99,
  };

  const canadianPlans = {
    basic: 149,
    plus: 199,
    premium: 249,
    additionalLocations: 99,
  };

  // ZAR display prices for South African (ZAF) clinics. These amounts must stay
  // aligned with the dedicated ZAR Stripe prices above (12578976635).
  const zafPlans = {
    basic: 2399,
    plus: 3199,
    premium: 3999,
    additionalLocations: 1599,
  };

  return {
    prices,
    planNames,
    planTypes,
    planNamesForSalesforce,
    allPlansIds,
    additionalLocationsIds,
    pricesToProducts,
    productsToPlans,
    plans,
    canadianPlans,
    zafPlans,
  };
};
